'use client';

import { c as _c } from "react/compiler-runtime";
import { useClaimMilestone } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Alert02Icon, CheckmarkCircle02Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { StatusPill } from '@/components/ui/status-pill';
import { onEvent } from '@/events';
import { logger } from '@/lib/telemetry/logger';
import { confettiOriginFromElement, fireRewardBurst } from '@/lib/ui/confetti';
import { PointsChip } from './points-chip';
interface ClaimAllMilestone {
  id: string;
  title: string;
  rewardPoints: string;
  reached: boolean;
  claimed: boolean;
}
type ClaimOutcome = 'claimed' | 'failed';
interface ClaimAllMilestonesProps {
  milestones: readonly ClaimAllMilestone[];
  /** Refresh milestones + points once a batch settles, so balances and claim states update. */
  onSettled: () => void;
}

/**
 * "Claim all" for every reached, unclaimed milestone. The included claims are
 * frozen when the sheet opens so the 20s milestones poll can't reshuffle the
 * list under the user. Claims are idempotent per milestone and few enough to
 * fit the write rate limit, so they go out together; a failed claim stays in
 * the sheet and Retry re-sends only the failures, never the claims that landed.
 */
export function ClaimAllMilestones(t0) {
  const $ = _c(99);
  const {
    milestones,
    onSettled
  } = t0;
  const t = useTranslations("rewards.milestones.claimAll");
  const locale = useLocale();
  const claimMilestone = useClaimMilestone();
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [included, setIncluded] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [outcomes, setOutcomes] = useState(t2);
  const [running, setRunning] = useState(false);
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== claimMilestone || $[3] !== included || $[4] !== locale || $[5] !== milestones || $[6] !== onSettled || $[7] !== open || $[8] !== outcomes || $[9] !== running || $[10] !== t) {
    const claimable = milestones.filter(_temp);
    let t21;
    if ($[30] !== included || $[31] !== outcomes) {
      let t22;
      if ($[33] !== outcomes) {
        t22 = m_0 => outcomes[m_0.id] !== "claimed";
        $[33] = outcomes;
        $[34] = t22;
      } else {
        t22 = $[34];
      }
      t21 = included.filter(t22);
      $[30] = included;
      $[31] = outcomes;
      $[32] = t21;
    } else {
      t21 = $[32];
    }
    const remaining = t21;
    let t22;
    if ($[35] !== included || $[36] !== outcomes) {
      let t23;
      if ($[38] !== outcomes) {
        t23 = m_1 => outcomes[m_1.id] === "failed";
        $[38] = outcomes;
        $[39] = t23;
      } else {
        t23 = $[39];
      }
      t22 = included.filter(t23);
      $[35] = included;
      $[36] = outcomes;
      $[37] = t22;
    } else {
      t22 = $[37];
    }
    const failedCount = t22.length;
    const totalPts = included.reduce(_temp2, BigInt(0));
    const openSheet = () => {
      setIncluded(claimable);
      setOutcomes({});
      setOpen(true);
    };
    let claimRemaining;
    if ($[40] !== claimMilestone || $[41] !== onSettled || $[42] !== remaining) {
      claimRemaining = async origin => {
        setRunning(true);
        const results = await Promise.all(remaining.map(async m_3 => {
          const outcome = await claimOne(m_3.id);
          setOutcomes(prev => ({
            ...prev,
            [m_3.id]: outcome
          }));
          return {
            id: m_3.id,
            outcome
          };
        }));
        setRunning(false);
        onSettled();
        const failedIds = results.flatMap(_temp3);
        if (failedIds.length < remaining.length) {
          fireRewardBurst(origin);
        }
        if (failedIds.length === 0) {
          setOpen(false);
          return;
        }
        logger.warn("Rewards claim-all left milestones unclaimed", {
          attempted: remaining.length,
          failed: failedIds.length,
          failedMilestoneIds: failedIds
        });
      };
      const claimOne = async id => {
        try {
          await claimMilestone.mutateAsync({
            id
          });
          onEvent("rewards_milestone_claimed", {
            milestoneId: id
          });
          return "claimed";
        } catch {
          return "failed";
        }
      };
      $[40] = claimMilestone;
      $[41] = onSettled;
      $[42] = remaining;
      $[43] = claimRemaining;
    } else {
      claimRemaining = $[43];
    }
    t20 = claimable.length > 0 && <AnimatedButton data-testid="claim-all-milestones" className="w-full" onClick={openSheet}>{t("button", {
        count: claimable.length
      })}</AnimatedButton>;
    T0 = SilkSheet;
    t11 = open;
    t12 = setOpen;
    t13 = "detached";
    t14 = "md";
    if ($[44] !== t) {
      t15 = t("title");
      $[44] = t;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    t16 = t("description", {
      count: included.length,
      pts: totalPts.toLocaleString(locale)
    });
    t17 = running;
    t18 = _temp4;
    let t23;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = () => setOpen(false);
      $[46] = t23;
    } else {
      t23 = $[46];
    }
    let t24;
    if ($[47] !== t) {
      t24 = t("cancel");
      $[47] = t;
      $[48] = t24;
    } else {
      t24 = $[48];
    }
    let t25;
    if ($[49] !== running || $[50] !== t24) {
      t25 = <AnimatedButton variant="secondary" onClick={t23} disabled={running}>{t24}</AnimatedButton>;
      $[49] = running;
      $[50] = t24;
      $[51] = t25;
    } else {
      t25 = $[51];
    }
    let t26;
    if ($[52] !== claimRemaining) {
      t26 = e => {
        claimRemaining(confettiOriginFromElement(e.currentTarget));
      };
      $[52] = claimRemaining;
      $[53] = t26;
    } else {
      t26 = $[53];
    }
    t19 = <ModalFooter>{t25}<AnimatedButton data-testid="claim-all-milestones-confirm" onClick={t26} disabled={running || remaining.length === 0} loading={running}>{running ? t("claiming") : failedCount > 0 ? t("retry", {
          count: failedCount
        }) : t("confirm", {
          pts: totalPts.toLocaleString(locale)
        })}</AnimatedButton></ModalFooter>;
    t7 = "flex flex-col gap-2 pb-4";
    t8 = "claim-all-milestones-list";
    if ($[54] !== failedCount || $[55] !== running || $[56] !== t) {
      t9 = failedCount > 0 && !running && <p className="text-caption text-feedback-error-content">{t("partialFailure", {
          count: failedCount
        })}</p>;
      $[54] = failedCount;
      $[55] = running;
      $[56] = t;
      $[57] = t9;
    } else {
      t9 = $[57];
    }
    if ($[58] !== included || $[59] !== locale || $[60] !== outcomes || $[61] !== t) {
      let t27;
      if ($[63] !== locale || $[64] !== outcomes || $[65] !== t) {
        t27 = m_4 => <div key={m_4.id} data-testid={`claim-all-item-${m_4.id}`} className="flex items-center justify-between gap-2 rounded-panel-row border px-3 py-2.5"><span className="min-w-0 flex-1 truncate text-body text-foreground-primary">{m_4.title}</span><ClaimOutcomeBadge outcome={outcomes[m_4.id]} pts={t("pts", {
            pts: Number(m_4.rewardPoints).toLocaleString(locale)
          })} /></div>;
        $[63] = locale;
        $[64] = outcomes;
        $[65] = t;
        $[66] = t27;
      } else {
        t27 = $[66];
      }
      t10 = included.map(t27);
      $[58] = included;
      $[59] = locale;
      $[60] = outcomes;
      $[61] = t;
      $[62] = t10;
    } else {
      t10 = $[62];
    }
    t5 = "flex items-center justify-between gap-2 px-3 pt-1 text-body font-medium";
    let t27;
    if ($[67] !== t) {
      t27 = t("total");
      $[67] = t;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    if ($[69] !== t27) {
      t6 = <span className="text-foreground-secondary">{t27}</span>;
      $[69] = t27;
      $[70] = t6;
    } else {
      t6 = $[70];
    }
    t3 = "tabular-nums text-foreground-primary";
    t4 = t("pts", {
      pts: totalPts.toLocaleString(locale)
    });
    $[2] = claimMilestone;
    $[3] = included;
    $[4] = locale;
    $[5] = milestones;
    $[6] = onSettled;
    $[7] = open;
    $[8] = outcomes;
    $[9] = running;
    $[10] = t;
    $[11] = T0;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
    $[17] = t15;
    $[18] = t16;
    $[19] = t17;
    $[20] = t18;
    $[21] = t19;
    $[22] = t20;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    T0 = $[11];
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t13 = $[15];
    t14 = $[16];
    t15 = $[17];
    t16 = $[18];
    t17 = $[19];
    t18 = $[20];
    t19 = $[21];
    t20 = $[22];
    t3 = $[23];
    t4 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t21;
  if ($[71] !== t3 || $[72] !== t4) {
    t21 = <span className={t3}>{t4}</span>;
    $[71] = t3;
    $[72] = t4;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== t21 || $[75] !== t5 || $[76] !== t6) {
    t22 = <div className={t5}>{t6}{t21}</div>;
    $[74] = t21;
    $[75] = t5;
    $[76] = t6;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== t10 || $[79] !== t22 || $[80] !== t7 || $[81] !== t8 || $[82] !== t9) {
    t23 = <div className={t7} data-testid={t8}>{t9}{t10}{t22}</div>;
    $[78] = t10;
    $[79] = t22;
    $[80] = t7;
    $[81] = t8;
    $[82] = t9;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== T0 || $[85] !== t11 || $[86] !== t12 || $[87] !== t13 || $[88] !== t14 || $[89] !== t15 || $[90] !== t16 || $[91] !== t17 || $[92] !== t18 || $[93] !== t19 || $[94] !== t23) {
    t24 = <T0 open={t11} onOpenChange={t12} variant={t13} size={t14} title={t15} description={t16} guardDismiss={t17} onDismissAttempt={t18} footer={t19}>{t23}</T0>;
    $[84] = T0;
    $[85] = t11;
    $[86] = t12;
    $[87] = t13;
    $[88] = t14;
    $[89] = t15;
    $[90] = t16;
    $[91] = t17;
    $[92] = t18;
    $[93] = t19;
    $[94] = t23;
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] !== t20 || $[97] !== t24) {
    t25 = <>{t20}{t24}</>;
    $[96] = t20;
    $[97] = t24;
    $[98] = t25;
  } else {
    t25 = $[98];
  }
  return t25;
}
function _temp4() {}
function _temp3(r) {
  return r.outcome === "failed" ? [r.id] : [];
}
function _temp2(sum, m_2) {
  return sum + BigInt(m_2.rewardPoints);
}
function _temp(m) {
  return m.reached && !m.claimed;
}
function ClaimOutcomeBadge(t0) {
  const $ = _c(12);
  const {
    outcome,
    pts
  } = t0;
  const t = useTranslations("rewards.milestones.claimAll");
  if (outcome === "claimed") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Icon icon={CheckmarkCircle02Icon} className="size-3.5" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== t) {
      t2 = t("claimed");
      $[1] = t;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <StatusPill tone="success" size="sm">{t1}{t2}</StatusPill>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (outcome === "failed") {
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Icon icon={Alert02Icon} className="size-3.5" />;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== t) {
      t2 = t("failed");
      $[6] = t;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== t2) {
      t3 = <StatusPill tone="destructive" size="sm">{t1}{t2}</StatusPill>;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t1;
  if ($[10] !== pts) {
    t1 = <PointsChip>{pts}</PointsChip>;
    $[10] = pts;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}